/**
 * Queue tab — pure filter/segment helpers. Two axes: the SEGMENT (queued /
 * testing / eliminated — wire state `removed`) and the LAYER (all / paths /
 * landers / offers). Also the per-segment row counts for the pill labels.
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  FlowLayer,
  QueueElement,
  QueueElementState,
  QueueListResult,
} from '@trafficpulse/shared';

import { filterByStatus, type QueueStatusFilter } from './status/path-status';

/** Primary axis — mirrors the element lifecycle state. */
export type QueueSegment = QueueElementState;
/** Secondary axis — 'all' passes every layer through. */
export type QueueLayerFilter = 'all' | FlowLayer;

export const QUEUE_SEGMENTS: ReadonlyArray<{ value: QueueSegment; label: string }> = [
  { value: 'waiting', label: 'Waiting' },
  { value: 'queued', label: 'Queue' },
  { value: 'testing', label: 'Live' },
  { value: 'removed', label: 'Eliminated' },
];

export const QUEUE_LAYERS: ReadonlyArray<{ value: QueueLayerFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'paths', label: 'Paths' },
  { value: 'landers', label: 'Landers' },
  { value: 'offers', label: 'Offers' },
];

/**
 * Layers gated behind "Available soon" for the paths-focused launch. The queue
 * only exposes paths for now — landers/offers rows are hidden and their pills
 * show a placeholder. Empty this array (nothing else to change) to re-enable
 * the full lander/offer queue. Do not delete.
 */
export const QUEUE_LAYERS_COMING_SOON: ReadonlyArray<FlowLayer> = ['landers', 'offers'];

/** True when a layer is gated behind "Available soon". */
export function isLayerComingSoon(layer: QueueLayerFilter): boolean {
  return layer !== 'all' && QUEUE_LAYERS_COMING_SOON.includes(layer);
}

/** The elements of one segment, straight from the grouped queue result. */
export function segmentElements(
  data: QueueListResult | undefined,
  segment: QueueSegment,
): QueueElement[] {
  if (!data) return [];
  // `?? []` guards a pre-deploy cached response that predates the waiting bucket.
  if (segment === 'waiting') return data.waiting ?? [];
  if (segment === 'queued') return data.queued;
  if (segment === 'testing') return data.testing;
  return data.removed;
}

/** Keep only elements matching the layer filter ('all' passes everything). */
export function filterByLayer(
  elements: QueueElement[],
  layer: QueueLayerFilter,
): QueueElement[] {
  if (layer === 'all') return elements;
  return elements.filter((el) => el.layer === layer);
}

/**
 * Free-text match over the fields a row is actually identified by.
 *
 * The tracker id is included because three paths called Greenluck, Greenluck BB
 * and Greenluck Sin are told apart by "#1396" and nothing else — the same
 * reason the "replaces X" chips carry it.
 */
function matchesTerm(el: QueueElement, needle: string): boolean {
  const haystack = [
    el.itemName,
    el.itemId,
    el.poolName,
    el.rule?.name,
  ];
  return haystack.some((v) => v?.toLowerCase().includes(needle));
}

/** Keep only elements matching a search term (blank passes everything). */
export function filterBySearch(
  elements: QueueElement[],
  search: string,
): QueueElement[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return elements;
  return elements.filter((el) => matchesTerm(el, needle));
}

/** Narrowings that apply to what is LISTED but never to the pill counts. */
export interface VisibleRowsOptions {
  status?: QueueStatusFilter;
  search?: string;
}

/**
 * The rows the table ACTUALLY renders for a segment: the layer filter applied,
 * and gated ("available soon") layers dropped — they are never displayed, so
 * counting them would make a pill disagree with the table under it.
 *
 * `status` and `search` both default to off, so the segment pills keep counting
 * every row — narrowing to Disabled, or typing in the search box, should not
 * make the "Live" pill shrink to match. The listing says "Showing N of M"
 * instead, which is the honest way to report a narrowing.
 *
 * One options bag rather than more positional arguments, so the two callers
 * that want unfiltered counts keep reading as such.
 */
export function visibleRows(
  data: QueueListResult | undefined,
  segment: QueueSegment,
  layer: QueueLayerFilter,
  options: VisibleRowsOptions = {},
): QueueElement[] {
  const rows = filterByLayer(segmentElements(data, segment), layer).filter(
    (el) => !QUEUE_LAYERS_COMING_SOON.includes(el.layer),
  );
  return filterBySearch(
    filterByStatus(rows, options.status ?? 'all'),
    options.search ?? '',
  );
}

/**
 * Row count per segment (drives the "Queue 3" pill labels), for the CURRENTLY
 * SELECTED layer — pick Paths and every pill counts paths only. Derived from the
 * same `visibleRows` the table renders, so a pill can never disagree with it.
 */
export function segmentCounts(
  data: QueueListResult | undefined,
  layer: QueueLayerFilter = 'all',
): Record<QueueSegment, number> {
  const count = (segment: QueueSegment) => visibleRows(data, segment, layer).length;
  return {
    waiting: count('waiting'),
    queued: count('queued'),
    testing: count('testing'),
    removed: count('removed'),
  };
}

/** Promotion order — mirrors the backend engine: 1 = highest, null last, oldest first. */
export function compareQueueOrder(a: QueueElement, b: QueueElement): number {
  if (a.priority !== b.priority) {
    if (a.priority === null) return 1;
    if (b.priority === null) return -1;
    return a.priority - b.priority;
  }
  const ta = a.queuedAt ? Date.parse(a.queuedAt) : Number.POSITIVE_INFINITY;
  const tb = b.queuedAt ? Date.parse(b.queuedAt) : Number.POSITIVE_INFINITY;
  return ta - tb;
}

/**
 * bankIds of the element that would be promoted NEXT for each layer — the top
 * of the queue per paths/landers/offers. Highlighted so you can see the actual
 * next-in-line at a glance.
 */
export function nextUpByLayer(queued: QueueElement[]): Set<string> {
  const best = new Map<FlowLayer, QueueElement>();
  for (const el of queued) {
    const cur = best.get(el.layer);
    if (!cur || compareQueueOrder(el, cur) < 0) best.set(el.layer, el);
  }
  return new Set(Array.from(best.values(), (e) => e.bankId));
}
